import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useProvideAppSocket } from "../hooks/useAppSocket";
import { useBadgeCounters } from "../hooks/useBadgeCounters";

function ConversationList({ threads }) {
  const { ws, status } = useProvideAppSocket();
  const { threadCounters } = useBadgeCounters({ ws, status });
  const { t_id } = useParams();
  const activeThreadId = t_id ? Number(t_id) : null;
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  // Close on ESC
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setIsOpen(false);
    }
    if (isOpen) {
      window.addEventListener("keydown", onKey);
    }

    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen]);

  if (!Array.isArray(threads) || !threads || threads.length === 0) {
    return (
      <div
        className={[
          "p-4 border-r border-grey-200",
          "sm:w-[72px] xl:w-[250px]",
          "transition-[transform,width] duration-200",
        ].join(" ")}
      >
        <h2 className="hidden xl:flex text-lg font-bold mb-6">Messages</h2>
        <p className="hidden xl:flex space-y-5 w-full text-sm">No Messages...</p>
      </div>
    );
  }

  return (
    <div className="p-4 border-r border-gray-200 sm:w-[72px] xl:w-[250px] transition-[transform,width] duration-200">
      {/* Sidebar / Drawer */}
      <aside
        ref={panelRef}
        id="message-sidebar"
        className={[
          "absolute inset-y-0 left-0 z-20 bg-white",
          "transition-transform duration-200 ease-out",
          isOpen ? "translate-x-0" : "-translate-x-full",
          isOpen && "border",
          "xl:static xl:translate-x-0 xl:z-auto xl:flex xl:flex-col",
        ].join(" ")}
      >
        {/* Header (sticky inside sidebar) */}
        <div className="sticky top-0 z-10 bg-white border-b p-4 flex items-center justify-between xl:block">
          <h2 className="text-lg font-semibold truncate">Messages</h2>
          {/* Close button (mobile only) */}
          <button
            type="button"
            className="xl:hidden inline-flex items-center justify-center rounded-md p-2 hover:bg-gray-100"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
            aria-controls="event-sidebar"
            aria-expanded={isOpen}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 min-w-0 overflow-y-auto p-4" aria-label="Threads">
          <ul className="space-y-3">
            {threads.map((thread) => {
              const participants = thread.participants || [];
              let default_profile_pic = "";
              let defaultTitle = thread.title || "";

              // TODO: The default pic for groups is just the first participant
              if (thread.is_group && !thread.title) {
                if (participants.length >= 3) {
                  defaultTitle = `${participants[0].username}, ${participants[1].username}, +others`;
                  default_profile_pic = participants[0].profile_pic_url;
                } else if (participants.length === 2) {
                  defaultTitle = `${participants[0].username}, ${participants[1].username}`;
                  default_profile_pic = participants[0].profile_pic_url;
                } else if (participants.length === 1) {
                  defaultTitle = participants[0].username;
                  default_profile_pic = participants[0].profile_pic_url;
                }
              } else if (!thread.is_group && !thread.title && participants[0]) {
                defaultTitle = participants[0].username;
                default_profile_pic = participants[0].profile_pic_url;
              }

              const unread = threadCounters?.[String(thread.id)] ?? thread.unread_count ?? 0;

              const isActive = activeThreadId === thread.id;

              return (
                <li key={thread.id}>
                  <Link
                    to={`/messages/t/${thread.id}`}
                    className={[
                      "group flex items-center w-full rounded px-2 py-2 transition-colors",
                      isActive
                        ? "bg-gray-300 text-black font-semibold"
                        : "hover:bg-gray-100 text-gray-700",
                    ].join(" ")}
                  >
                    <div className="flex items-center w-full gap-3">
                      {/* Avatar wrapper */}
                      <div className="relative flex-shrink-0 w-9 h-9 flex p-[2px] bg-white rounded-full">
                        <img
                          src={default_profile_pic || `${process.env.PUBLIC_URL}/logo512.png`}
                          alt="Profile"
                          className="w-8 h-8 rounded-full object-cover border border-black ring-2 ring-white"
                        />

                        {unread > 0 && (
                          <span
                            className="absolute -top-1 -left-3 flex h-5 w-5 items-center justify-center rounded-full border border-black bg-purple-600 text-white text-xs"
                            aria-label={`${unread} unread`}
                          >
                            {unread}
                          </span>
                        )}
                      </div>

                      {/* Text area */}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{defaultTitle}</p>
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* Drawer open button (only visible below xl) */}
      <div className="flex border-b items-center justify-center mb-3 xl:hidden">
        <button
          type="button"
          className="flex items-center justify-center rounded-md p-2 hover:bg-gray-100"
          onClick={() => setIsOpen(true)}
          aria-label="Open menu"
          aria-controls="event-sidebar"
          aria-expanded={isOpen}
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {!isOpen && (
        <nav className="flex items-center justify-center xl:hidden" aria-label="Threads">
          <ul className="space-y-3">
            {threads.map((thread) => {
              const participants = thread.participants || [];
              let defaultTitle = thread.title || "";
              let default_profile_pic = "";

              // TODO: The default pic for groups is just the first participant
              if (thread.is_group && !thread.title) {
                if (participants.length >= 3) {
                  defaultTitle = `${participants[0].username}, ${participants[1].username}, +others`;
                  default_profile_pic = participants[0].profile_pic_url;
                } else if (participants.length === 2) {
                  defaultTitle = `${participants[0].username}, ${participants[1].username}`;
                  default_profile_pic = participants[0].profile_pic_url;
                } else if (participants.length === 1) {
                  defaultTitle = participants[0].username;
                  default_profile_pic = participants[0].profile_pic_url;
                }
              } else if (!thread.is_group && !thread.title && participants[0]) {
                defaultTitle = participants[0].username;
                default_profile_pic = participants[0].profile_pic_url;
              }

              const unread = threadCounters?.[String(thread.id)] ?? thread.unread_count ?? 0;

              const isActive = activeThreadId === thread.id;

              return (
                <li key={thread.id}>
                  <Link
                    to={`/messages/t/${thread.id}`}
                    className={[
                      "group flex items-center w-full rounded px-2 py-2 transition-colors",
                      isActive ? "bg-gray-300 text-white" : "hover:bg-gray-100 text-gray-700",
                    ].join(" ")}
                  >
                    <div className="flex items-center w-full">
                      {/* Avatar wrapper */}
                      <div className="relative flex-shrink-0 w-9 h-9 flex p-[2px] bg-white rounded-full">
                        <img
                          src={default_profile_pic || `${process.env.PUBLIC_URL}/logo512.png`}
                          alt="Profile"
                          className="w-8 h-8 rounded-full object-cover border border-black flex-shrink-0 bg-white ring-2 ring-white"
                        />

                        {unread > 0 && (
                          <span className="absolute -top-1 -left-3 flex h-5 w-5 items-center justify-center rounded-full border border-black bg-purple-600 text-white text-xs">
                            {unread}
                          </span>
                        )}
                      </div>

                      {/* Optional: text area */}
                      <div className="flex-1">{/* thread name, preview, etc */}</div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </div>
  );
}

export default ConversationList;
